import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Button } from "@material-ui/core";

const presentationAnimationDurationInMs = 250;

const DismissGestureCatcher = styled.div`
  position: fixed;
  z-index: 900;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const StyledMenuList = styled.ul`
  position: absolute;
  z-index: 1000;
  top: 100%;
  
  &.align-top-leading {
    left: 0;
    top: 100%;
    transform-origin: top left;
  }
  
  &.align-top-trailing {
    right: 0;
    top: 100%;
    transform-origin: top right;
  }
  
  &.align-bottom-leading {
    left: 0;
    top: -100%;
    transform-origin: bottom left;
  }
  
  &.align-bottom-trailing {
    right: 0;
    top: -100%;
    transform-origin: bottom right;
  }
  
  list-style: none;
  padding: 0;
  margin: 0;
  background: white;
  border-radius: 4px;
  border: 1px solid #DADCE0;
  box-shadow: 0px 10px 20px rgba(173, 174, 174, 0.25);
  overflow: hidden;
  
  transition: transform ${ presentationAnimationDurationInMs }ms ease, opacity 0.25s ease;
  transform: scale(0);
  opacity: 0;
  
  &.presented {
    transform: scale(1);
    opacity: 1;
  }
`;

const StyledMenuListItem = styled.li`  
  & button {
    width: 100%;
    min-width: 225px;
    appearance: none;
    background: none;
    padding: 11px;
    font-family: 'Work Sans';
    font-size: 14px;
    font-weight: 500;
    color: #005AA9;
    text-transform: none;
    border: 0;
    border-radius: 0px;
    justify-content: flex-start;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #DADCE0;
  }
`;

export interface ContextualMenuItemProps {
  title: string;
  onClick: () => void;
}

const ContextualMenuItem = (props: ContextualMenuItemProps) => {
  const { title, onClick } = props;
  return (
    <StyledMenuListItem>
      <Button onClick={ onClick }>{ title }</Button>
    </StyledMenuListItem>
  );
};

export enum MenuAlignmentMode {
  TopLeading,// Align the top-left corner of the menu to the bottom-left corner of its container
  TopTrailing,// Align the top-right corner of the menu to the bottom-right corner of its container
  BottomLeading,// Align the bottom-left corner of the menu to the bottom-left corner of its container
  BottomTrailing// Align the bottom-right corner of the menu to the bottom-right corner of its container
}

interface ContextualMenuProps {
  alignmentMode?: MenuAlignmentMode;
  menuItems: ContextualMenuItemProps[];
  onDismiss: () => void;
}

const ContextualMenu = (props: ContextualMenuProps) => {
  const { menuItems, alignmentMode, onDismiss } = props;
  
  const [isPresented, setIsPresented] = useState<boolean>(false);
  useEffect(() => {
    setIsPresented(true);
  }, [setIsPresented]);
  
  const dismissWithAnimation = (completion: () => void) => {
    setIsPresented(false);
    setTimeout(completion, presentationAnimationDurationInMs);
  };
  
  const handleEscapeKeypress = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      dismissWithAnimation(onDismiss);
    }
  };
  
  const handleDismissClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      dismissWithAnimation(onDismiss);
    }
  };
  
  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKeypress, false);
    return () => document.removeEventListener("keydown", handleEscapeKeypress, false);
  }, [handleEscapeKeypress]);
  
  let alignmentClassString;
  switch (alignmentMode) {
    case MenuAlignmentMode.TopTrailing:
      alignmentClassString = "align-top-trailing";
      break;
    case MenuAlignmentMode.BottomLeading:
      alignmentClassString = "align-bottom-leading";
      break;
    case MenuAlignmentMode.BottomTrailing:
      alignmentClassString = "align-bottom-trailing";
      break;
    case MenuAlignmentMode.TopLeading:
    default:
      alignmentClassString = "align-top-leading";
      break;
  }
    
  return (
    <>
      <DismissGestureCatcher onClick={handleDismissClick} />
      
      <StyledMenuList className={`${ alignmentClassString } ${ isPresented && "presented" }`}>
        { menuItems.map((item: ContextualMenuItemProps, index: number) => <ContextualMenuItem { ...item } key={ index } />) }
      </StyledMenuList>
    </>
  );
};

export default ContextualMenu;
